import React from 'react';
import { Points } from 'components/Points/Points';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { TradeAlert, TradeAlertRequester } from 'store/services/tradeAlerts/tradeAlertsApi';
import { UserIdentityBlock } from 'components/UserIdentityBlock';
import { AlertCardTile } from './AlertCardTile';
import './AlertTradeGroup.css';

/** All of one requester's alerts, plus the derived group-level summary fields. */
export interface AlertGroup {
  requester: TradeAlertRequester;
  alerts: TradeAlert[];
  count: number;
  /** Sum of every card's pointValue — the total package value. */
  totalPoints: number;
  /** Most recent availableSince in the group — the group's sort key + header time. */
  mostRecent: string;
}

interface AlertTradeGroupProps {
  group: AlertGroup;
  variant: 'all' | 'hidden';
  onSend: (alert: TradeAlert) => void;
  onHide: (alert: TradeAlert) => void;
  onUnhide: (alert: TradeAlert) => void;
}

/**
 * "By trader" view: every alert from a single requester in one container. The requester
 * identity block (avatar/flag/username/balance/hover) is shown once as a header, followed
 * by a summary (card count + total package value), then the individual card tiles — each
 * keeping its own per-card Send and dismiss.
 */
export const AlertTradeGroup: React.FC<AlertTradeGroupProps> = ({
  group,
  variant,
  onSend,
  onHide,
  onUnhide,
}) => (
  <GlassContainer variant="cardDetails" className="alert-trade-group">
    <div className="alert-trade-group-header">
      <UserIdentityBlock
        username={group.requester.username}
        avatarUrl={group.requester.avatarUrl}
        country={group.requester.country}
        points={group.requester.points}
        isElite={group.requester.isElite}
        showAnimatedBorder={group.requester.showAnimatedBorder}
        borderColor1={group.requester.borderColor1}
        borderColor2={group.requester.borderColor2}
      />
      <div className="alert-trade-group-summary">
        <span className="alert-trade-group-time">{formatRelativeTime(group.mostRecent)}</span>
        <div className="alert-trade-group-totals">
          <span className="alert-trade-group-count">
            {group.count} {group.count === 1 ? 'card' : 'cards'}
          </span>
          <Points points={group.totalPoints} type="small" />
        </div>
      </div>
    </div>

    <div className="alert-trade-group-cards">
      {group.alerts.map((alert) => (
        <AlertCardTile
          key={alert.id}
          alert={alert}
          variant={variant}
          onSend={onSend}
          onHide={onHide}
          onUnhide={onUnhide}
          grouped
        />
      ))}
    </div>
  </GlassContainer>
);

export default AlertTradeGroup;
